import { CURRENT_CHAIN } from '@hyperswap/addresses'
import { WETH9 } from '@uniswap/sdk-core'
import React, { useState } from 'react'
import { LeverageItem } from '../styled'
import PoolLogo from '../PoolLogo'
import Tag from 'components/Tag'
import { formattedFeeAmount } from 'utils'
import { roundToDecimalPlace } from 'utils/data'
import { formatUnits } from 'ethers/lib/utils'
import TokenLogo from '../TokenLogo'
import TagButton from 'components/TagButton'
import KeikoLogo from '../../../assets/keiko.jpg'
import LHYPELogo from '../../../assets/lhype-logo.svg'
import { useHistory } from 'react-router-dom'
import HyperpieLogo from '../../../assets/hyperpie.jpg'
import {
  VolumeUpIcon,
  VolumeDownIcon,
  CopyIcon,
  CopyCheckIcon,
  FireIcon,
  AddLiquidityIcon,
  SwapIcon,
  HyperswapIcon,
  HypurrFiIcon,
  InfoIcon,
} from './icons'
import IncentiveTooltip from 'components/IncentiveTooltip'
import { useCampaigns } from 'hooks/merkle/useCampaigns'

interface PoolCardProps {
  pool: any
  rewards: any
}
const PoolCard: React.FC<PoolCardProps> = ({ pool, rewards }) => {
  const history = useHistory()
  const [copyStatus, setCopyStatus] = useState<string | null>(null)
  const saveLink = (address: string) => {
    if (address) {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard
          .writeText(address)
          .then(() => {
            setCopyStatus('Copied!')
            setTimeout(() => setCopyStatus(null), 5000)
          })
          .catch(() => {
            // Fallback to manual copy if writeText fails
            fallbackCopyTextToClipboard(address)
          })
      } else {
        // Fallback for older browsers
        fallbackCopyTextToClipboard(address)
      }
    }
  }

  const fallbackCopyTextToClipboard = (text: string) => {
    const textArea = document.createElement('textarea')
    textArea.value = text
    textArea.style.position = 'fixed' // Prevent scrolling to bottom of page in Microsoft Edge.
    document.body.appendChild(textArea)
    textArea.focus()
    textArea.select()

    try {
      const successful = document.execCommand('copy')
      if (successful) {
        setCopyStatus('Copied!')
      } else {
        setCopyStatus('Copy failed')
      }
    } catch (err) {
      console.error('Fallback: Oops, unable to copy', err)
    }

    document.body.removeChild(textArea)
    setTimeout(() => setCopyStatus(null), 5000)
  }

  const pairAddress = pool.pairAddress
  const { campaigns } = useCampaigns(pairAddress)

  const isHypurrFiPoints =
    ((pool.token0.token0Symbol === 'WHYPE' || pool.token1.token1Symbol === 'WHYPE') &&
      (pool.token0.token0Symbol === 'USDXL' || pool.token1.token1Symbol === 'USDXL')) ||
    ((pool.token0.token0Symbol === 'KEI' || pool.token1.token1Symbol === 'KEI') &&
      (pool.token0.token0Symbol === 'USDXL' || pool.token1.token1Symbol === 'USDXL'))
  const LHYPEPoints = pool.token0.token0Symbol === 'LHYPE' || pool.token1.token1Symbol === 'LHYPE'
  const isKEIPoints =
    (pool.token0.token0Symbol === 'KEI' || pool.token1.token1Symbol === 'KEI') && pool.token1.token1Symbol !== 'USDXL'
  const isVolumeIncreasing = pool.prevH24 && pool.h24 ? parseFloat(pool.h24) - parseFloat(pool.prevH24) > 0 : null
  const isMagpiePoints = pool.token0.token0Symbol === 'mHYPE' || pool.token1.token1Symbol === 'mHYPE'

  const volumeIncreasingIcon =
    isVolumeIncreasing !== null ? isVolumeIncreasing ? <VolumeUpIcon /> : <VolumeDownIcon /> : <></>

  const addV2LPRoute = `/add${pool.version === 'v3' ? '/' : '/v2/'}${
    pool.token0.token0Address === WETH9[CURRENT_CHAIN].address ? 'HYPE' : pool.token0.token0Address
  }/${pool.token1.token1Address === WETH9[CURRENT_CHAIN].address ? 'HYPE' : pool.token1.token1Address}${
    pool.fee ? `/${pool.fee}` : ''
  }`

  const swapRoute = `/swap${pool.version === 'v3' ? '/' : '/v2/'}?inputCurrency=${
    pool.token0.token0Address === WETH9[CURRENT_CHAIN].address ? 'HYPE' : pool.token0.token0Address
  }&outputCurrency=${pool.token1.token1Address === WETH9[CURRENT_CHAIN].address ? 'HYPE' : pool.token1.token1Address}`

  const aprIsFire = pool?.apr && pool?.apr > 50
  const isIncentive = campaigns?.length > 0
  const isIncentiveAndAprFire = aprIsFire && isIncentive

  const incentiveInfoContent = () => {
    return (
      <>
        {campaigns.map((campaign) => {
          const startDate = new Date(Number(campaign.startTimestamp) * 1000)
          const endDate = new Date(Number(campaign.endTimestamp) * 1000)
          const amount = Number(campaign.amount) / Math.pow(10, campaign.rewardToken.decimals)

          return (
            <div key={campaign.id} className="campaign-card">
              <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                Earn <TokenLogo token={campaign.rewardToken.address} /> ${campaign.rewardToken.symbol}
              </span>
              <p>
                <b>
                  {roundToDecimalPlace(`${amount}`, '10')} {campaign.rewardToken.symbol}
                </b>{' '}
                distributed from <b>{startDate.toLocaleString()}</b> to <b>{endDate.toLocaleString()}</b>.
              </p>
            </div>
          )
        })}
      </>
    )
  }

  return (
    <LeverageItem
      key={pool.token0.token0Address}
      attribut={
        isIncentiveAndAprFire ? /* 'incentiveAndApr' */ 'incentive' : aprIsFire ? 'apr' : isIncentive ? 'incentive' : ''
      }
    >
      <div className="card-content">
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginLeft: '30px',
            marginBottom: '8px',
          }}
        >
          <PoolLogo tokenA={pool.token0.token0Address} tokenB={pool.token1.token1Address} size={48} />
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              flexDirection: 'column',
              justifyContent: 'flex-start',
            }}
          >
            <span
              style={{
                fontWeight: 500,
                fontSize: 16,
                textAlign: 'left',
                marginRight: 'auto',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
                gap: '8px',
                flexWrap: 'wrap',
              }}
            >
              {pool.token0.token0Symbol === 'WHYPE' ? 'HYPE' : pool.token0.token0Symbol} /{' '}
              {pool.token1.token1Symbol === 'WHYPE' ? 'HYPE' : pool.token1.token1Symbol}
              <TagButton onClick={() => saveLink(pool?.pairAddress)}>
                {copyStatus ? <CopyCheckIcon /> : <CopyIcon />}
              </TagButton>
              <Tag variant={pool.version === 'v3' ? 2 : 1} style={{ marginLeft: '4px' }}>
                <div style={{ padding: '0 2px' }}>{pool.version === 'v3' ? 'v3' : 'v2'}</div>
              </Tag>
              <Tag variant={1}>
                <div style={{ padding: '0 2px' }}>{pool.fee ? formattedFeeAmount(pool.fee) : '0.3'}%</div>
              </Tag>
            </span>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
                gap: '8px',
                flexWrap: 'wrap',
                width: '100%',
              }}
            >
              {aprIsFire && <Tag variant={4}>Hot APR</Tag>}
              {isIncentive && (
                <IncentiveTooltip content={incentiveInfoContent()}>
                  <Tag variant={5}>
                    Incentivized <InfoIcon />
                  </Tag>
                </IncentiveTooltip>
              )}
              <Tag variant={1}>
                {HyperswapIcon()}
                Points
              </Tag>
              {isMagpiePoints && (
                <Tag variant={3} style={{ background: 'rgb(178, 242, 253)', minWidth: 'unset' }}>
                  <img
                    src={HyperpieLogo}
                    style={{ width: '14px', borderRadius: '100px', marginRight: '2px' }}
                    alt="hyperpie logo"
                  />
                  2X Drops
                </Tag>
              )}
              {isHypurrFiPoints && (
                <Tag variant={3}>
                  {HypurrFiIcon()}
                  HypurrFi points
                </Tag>
              )}
              {isKEIPoints && (
                <Tag variant={3} style={{ background: 'rgb(178, 242, 253)', minWidth: 'unset' }}>
                  <img
                    src={KeikoLogo}
                    style={{ width: '14px', borderRadius: '100px', marginRight: '2px' }}
                    alt="kei logo"
                  />
                  KEIKO points
                </Tag>
              )}
              {LHYPEPoints && (
                <Tag variant={3} style={{ background: 'rgb(8, 28, 35)', minWidth: 'unset', color: 'white' }}>
                  <img
                    src={LHYPELogo}
                    style={{ width: '14px', borderRadius: '100px', marginRight: '2px' }}
                    alt="lhype logo"
                  />
                  LoopedHYPE points
                </Tag>
              )}
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '32px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '20px' }}>
            <span style={{ color: '#c3c5cb', fontWeight: 400, fontSize: '14px' }}>APR</span>
            <span
              style={{
                fontWeight: 500,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '4px',
              }}
              className="responsive-text"
            >
              {aprIsFire && <FireIcon />}
              {pool.apr ? `${roundToDecimalPlace(`${pool.apr}`, '2')}%` : 'N/A'}
            </span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '20px' }}>
            <span style={{ color: '#c3c5cb', fontWeight: 400, fontSize: '14px' }}>Daily Fees</span>
            <span style={{ fontWeight: 500 }} className="responsive-text">
              {pool.dailyFees ? `$${roundToDecimalPlace(`${pool.dailyFees}`, '2')}` : 'N/A'}
            </span>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '20px' }}>
            <span style={{ color: '#c3c5cb', fontWeight: 400, fontSize: '14px' }}>TVL</span>
            <span style={{ fontWeight: 500 }} className="responsive-text">
              {pool.totalTvlUSD ? `$${roundToDecimalPlace(`${pool.totalTvlUSD}`, '2')}` : 'N/A'}
            </span>
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '20px' }}>
          <span style={{ color: '#c3c5cb', fontWeight: 400, fontSize: '14px' }}>1D Volume</span>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              gap: '2px',
            }}
          >
            {volumeIncreasingIcon && volumeIncreasingIcon}{' '}
            <span style={{ fontWeight: 500 }} className="responsive-text">
              {pool.h24 ? `$${roundToDecimalPlace(`${pool.h24}`, '2')}` : 'N/A'}
            </span>
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '20px', flex: 1 }}>
          <span style={{ color: '#c3c5cb', fontWeight: 400, fontSize: '14px' }}>Reserve</span>
          <div style={{ display: 'flex', flexDirection: 'row', gap: '6px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <TokenLogo token={pool.token0.token0Address} />
              <span style={{ fontSize: '16px', fontWeight: 500 }}>
                {roundToDecimalPlace(`${formatUnits(pool.reserves.reserve0, pool.token0.token0Decimal)}`, '2')}{' '}
              </span>
            </div>
            <span style={{ padding: '0 8px' }}>/</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <TokenLogo token={pool.token1.token1Address} />
              <span style={{ fontSize: '16px', fontWeight: 500 }}>
                {roundToDecimalPlace(`${formatUnits(pool.reserves.reserve1, pool.token1.token1Decimal)}`, '2')}
              </span>
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '28px' }}>
          <TagButton
            onClick={() => {
              history.push(addV2LPRoute)
            }}
            style={{ width: '100%', fontSize: '14px', padding: '10px', height: 'unset' }}
          >
            <AddLiquidityIcon />
            Add Liq.
          </TagButton>
          <TagButton
            onClick={() => {
              history.push(swapRoute)
            }}
            style={{ width: '100%', fontSize: '14px', padding: '10px', height: 'unset' }}
          >
            <SwapIcon />
            Swap
          </TagButton>
        </div>
      </div>
    </LeverageItem>
  )
}
export default PoolCard
